/**
 * Shared prescription template payload shape for Mentor and Support template editors.
 * Persist/retrieve via template APIs; server prefers pdf template when generating reports.
 *
 * Payload fields:
 * - prescriptionTemplateType: "pdf" | "print"
 * - header: { imageId, customHeaderHeight }
 * - footer: { imageId, customFooterHeight }
 * - signatureDetails: { imageId, signaturePosition, showPractitionerName, practitionerNameCase, showLicenseDetails, richText }
 * - signature: legacy signature media id (fallback when signatureDetails not used)
 */

export const PRESCRIPTION_TEMPLATE_TYPE_PDF = 'pdf'
export const PRESCRIPTION_TEMPLATE_TYPE_PRINT = 'print'

const DEFAULT_SIGNATURE_DETAILS = {
  imageId: null,
  signaturePosition: 'right',
  showPractitionerName: true,
  practitionerNameCase: 'small',
  showLicenseDetails: false,
  richText: '',
}

/**
 * Builds the canonical payload for save API. Sends signatureDetails (object) and
 * keeps legacy signature (string mediaId) as backward fallback.
 * @param {Object} template - Template state from form (may have header, footer, signature, signatureDetails)
 * @returns {Object} Payload suitable for POST save (same shape for Mentor and Support)
 */
export function buildPrescriptionTemplatePayload(template) {
  if (!template || typeof template !== 'object') {
    return {}
  }
  const signatureDetails = template.signatureDetails && typeof template.signatureDetails === 'object'
    ? {
        imageId: template.signatureDetails.imageId ?? template.signature ?? null,
        signaturePosition: template.signatureDetails.signaturePosition ?? 'right',
        showPractitionerName: template.signatureDetails.showPractitionerName ?? true,
        practitionerNameCase: template.signatureDetails.practitionerNameCase ?? 'small',
        showLicenseDetails: template.signatureDetails.showLicenseDetails ?? false,
        richText: template.signatureDetails.richText ?? '',
      }
    : {
        ...DEFAULT_SIGNATURE_DETAILS,
        imageId: template.signature ?? null,
      }
  const legacySignature = signatureDetails.imageId || template.signature || null
  return {
    ...template,
    header: {
      ...(template.header || {}),
      imageId: template.header?.imageId ?? null,
      customHeaderHeight: template.header?.customHeaderHeight ?? null,
    },
    footer: {
      ...(template.footer || {}),
      imageId: template.footer?.imageId ?? null,
      customFooterHeight: template.footer?.customFooterHeight ?? null,
    },
    signatureDetails,
    signature: legacySignature,
  }
}

/**
 * Normalizes a template from the API so the form always has signatureDetails.
 * If the API returns signatureDetails, use it; otherwise derive from legacy signature.
 * @param {Object} template - Raw template from GET response
 * @returns {Object} Template with signatureDetails populated
 */
export function normalizePrescriptionTemplateFromApi(template) {
  if (!template || typeof template !== 'object') {
    return template
  }
  const signatureDetails = template.signatureDetails && typeof template.signatureDetails === 'object'
    ? {
        imageId: template.signatureDetails.imageId ?? template.signature ?? null,
        signaturePosition: template.signatureDetails.signaturePosition ?? 'right',
        showPractitionerName: template.signatureDetails.showPractitionerName ?? true,
        practitionerNameCase: template.signatureDetails.practitionerNameCase ?? 'small',
        showLicenseDetails: template.signatureDetails.showLicenseDetails ?? false,
        richText: template.signatureDetails.richText ?? '',
      }
    : {
        ...DEFAULT_SIGNATURE_DETAILS,
        imageId: template.signature ?? null,
      }
  return {
    ...template,
    signatureDetails,
  }
}

/**
 * Default payload for creating a new template. PDF-first: prescriptionTemplateType "pdf".
 */
export function getDefaultNewPrescriptionTemplatePayload({ prescriptionTemplateType = PRESCRIPTION_TEMPLATE_TYPE_PDF, clinicId } = {}) {
  return {
    prescriptionTemplateType: prescriptionTemplateType || PRESCRIPTION_TEMPLATE_TYPE_PDF,
    active: false,
    clinicId: clinicId === 'private' ? null : clinicId,
    header: { imageId: null, customHeaderHeight: null },
    footer: { imageId: null, customFooterHeight: null },
    signatureDetails: { ...DEFAULT_SIGNATURE_DETAILS },
    signature: null,
  }
}
